// src/services/analytics.js
//
// Analytics for Finding Your Voice, on the fleet's consent-aware GA4 rail
// (@ddd/analytics; profound-thinking is the reference implementation, and
// docs/ANALYTICS_STANDARD.md is the rule book). This is a general-tier app.
//
// Google measures nothing until the visitor says yes: @ddd/analytics injects
// the gtag loader only after consent, so before that the app makes no request
// to Google at all. Global Privacy Control is a standing no. (Cloudflare Web
// Analytics, injected at the edge when the host is proxied, counts visits in
// totals for everyone and needs no code here.)
//
// The measurement ID is a literal on purpose. It is public by nature (it
// appears in page source), and an env var silently switches analytics off in
// any build made without a .env. G-KKY56ZVRYS is the web stream created with
// the finding-your-voice-2701a Firebase project (Nigel, 2026-10-04).
//
// Firebase Analytics (getAnalytics) is deliberately NOT used: it starts
// sending the moment it is called, so it cannot honour a consent gate.
import {
  initAnalytics,
  setAnalyticsConsent,
  getStoredConsent,
  trackModuleComplete,
  trackAssessmentComplete,
  trackToolUsed,
} from '@ddd/analytics';

export const MEASUREMENT_ID = 'G-KKY56ZVRYS';

// The app's own record of the visitor's choice. Boot re-derives consent from
// this, and the Profile page's switch writes it.
export const PREFERENCES_KEY = 'fyv_preferences';

const readPreferences = () => {
  try {
    const stored = localStorage.getItem(PREFERENCES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/** The visitor's stored analytics decision. Defaults to false: opt-in, never opt-out. */
export function hasAnalyticsPreference() {
  return readPreferences().analytics === true;
}

/**
 * Persist the decision in the app's preferences, merged over whatever else is
 * stored there. The consent notice calls this through onDecision; without it a
 * "yes" on the notice would quietly revert at the next visit, because boot
 * reads this key.
 */
export function saveAnalyticsPreference(granted) {
  try {
    localStorage.setItem(
      PREFERENCES_KEY,
      JSON.stringify({ ...readPreferences(), analytics: granted === true }),
    );
  } catch {
    /* storage unavailable: the in-page decision still applies for this visit */
  }
}

/** The Profile page's switch: remember the choice and act on it at once. */
export function setAnalyticsChoice(granted) {
  saveAnalyticsPreference(granted);
  setAnalyticsConsent(granted === true);
}

/**
 * Call once at startup, before React mounts. Loads GA4 only if the visitor
 * has already opted in; otherwise it arms the helper and waits for a decision.
 */
export function startAnalytics() {
  initAnalytics({ measurementId: MEASUREMENT_ID, appId: 'finding-your-voice' });
  // Record a consent state only when a real decision exists. Writing 'denied'
  // for a visitor who has never been asked would erase the never-asked state
  // the first-visit consent notice keys on.
  if (getStoredConsent() !== null || hasAnalyticsPreference()) {
    setAnalyticsConsent(hasAnalyticsPreference());
  }
}

// Fleet-standard events. Identifiers only: the sanitiser in @ddd/analytics
// drops anything that is not a short id, so nothing a person writes in a tool
// or the journal can ride along. All three are no-ops until consent.
export const recordModuleComplete = (moduleNumber) => trackModuleComplete(`module-${moduleNumber}`);
export const recordAssessmentComplete = () => trackAssessmentComplete('speaking-assessment');
export const recordToolUsed = (toolId) => trackToolUsed(String(toolId));
